/**
 * selection-context.ts
 *
 * Captures what the user selected in the Tiptap editor and determines the best
 * way to replace it after the AI responds.
 *
 * Tiptap/ProseMirror works with integer positions in a document tree, not HTML
 * strings. Every node has a "depth" (how many levels deep it sits). Inserting
 * content at the wrong depth produces schema validation errors or a
 * "Position outside of fragment" range error. This file resolves the correct
 * positions and insertion mode before anything is sent to the AI or written back.
 *
 * REPLACEMENT STRATEGY
 * --------------------------------------------------
 * • document mode  → editor.setContent()   (whole-doc swap, no position arithmetic)
 * • all other modes → DOMParser.parseSlice({ context: $from }) + tr.replaceRange()
 *   parseSlice inspects the ancestor chain at $from and produces the correct
 *   openStart/openEnd so replaceRange works for every content shape:
 *   inline text inside a paragraph, full blocks, list items, cross-boundary ranges.
 *
 * MODES
 * -----
 * • document       – Ctrl+A / whole-doc selection. from=0 is a gap position that
 *                    cannot be used with replaceRange; handled via editor.setContent().
 * • singleListItem – Selection inside one <li>. The AI response's outer <ul>/<li>
 *                    wrappers are stripped; content is inserted inside the item node.
 * • multiListItems – Selection spans several <li>s in the same flat list.
 *                    The AI response's outer <ul>/<ol> wrapper is stripped so bare
 *                    <li> nodes are inserted at the list container level.
 * • blockRange     – Everything else: paragraphs, headings, inline text within a
 *                    block, or cross-boundary selections. AI response is inserted
 *                    verbatim; parseSlice handles depth matching automatically.
 */

import { type Editor, getHTMLFromFragment } from '@tiptap/core';
import type { Node as ProseMirrorNode, ResolvedPos } from '@tiptap/pm/model';

import { stripAiHighlightMarks } from '../../extensions/ai-highlight';

export type SelectionMode = 'document' | 'singleListItem' | 'multiListItems' | 'blockRange';

export interface SelectionContext {
  html: string; // HTML sent to the AI (may cover more than the visual highlight)
  from: number;
  to: number;
  mode: SelectionMode;
}

// ─── Position helpers ────────────────────────────────────────────────────────

/** Walks UP the ancestor chain; returns the depth of the innermost <li>, or null. */
function listItemDepth(pos: ResolvedPos): number | null {
  for (let depth = pos.depth; depth > 0; depth--) {
    if (pos.node(depth).type.name === 'listItem') return depth;
  }
  return null;
}

/** Walks DOWN the ancestor chain; returns the depth of the outermost <li>, or null. */
function outermostListItemDepth(pos: ResolvedPos): number | null {
  for (let depth = 1; depth <= pos.depth; depth++) {
    if (pos.node(depth).type.name === 'listItem') return depth;
  }
  return null;
}

function nearestBlockDepth(pos: ResolvedPos): number | null {
  for (let depth = pos.depth; depth > 0; depth--) {
    if (pos.node(depth).isBlock) return depth;
  }
  return null;
}

function clampPos(doc: ProseMirrorNode, pos: number): number {
  return Math.max(0, Math.min(pos, doc.content.size));
}

/**
 * Ctrl+A in Tiptap sets from=0 (a gap position before the first node) and
 * to=doc.content.size. Position 0 is outside any content node, so it cannot
 * be used safely with replaceRange. Detecting this early lets callers route to
 * a whole-document replacement instead.
 */
function isDocumentSelection(doc: ProseMirrorNode, from: number, to: number): boolean {
  return from === 0 && to === doc.content.size;
}

// ─── HTML normalisation ──────────────────────────────────────────────────────

/**
 * Strips outer list/item wrappers from the AI response so the content can be
 * inserted directly inside the existing <li> node.
 *
 * The browser's DOM parser is used here because it handles arbitrary nesting
 * (including nested lists) correctly — building a proper tree regardless of
 * how deeply the AI wrapped its response.
 *
 * The unwrap loop handles every shape the AI might return:
 *   <ul><li>content</li></ul>  → content
 *   <li>content</li>           → content
 *   <p>content</p>             → <p>content</p>  (valid inside <li>, left as-is)
 */
export function normalizeListHtml(html: string, ownerDoc: Document): string {
  const div = ownerDoc.createElement('div');
  div.innerHTML = html;

  const firstNonEmptyChild = (root: Element): Element | null => {
    for (const child of Array.from(root.children)) {
      if (child.textContent?.trim() || child.children.length > 0) return child;
    }
    return null;
  };

  // Keep unwrapping single-root ul/ol/li shells until we reach content that
  // can live directly inside a <li> (paragraphs, text, nested lists).
  while (div.childElementCount === 1) {
    const child = firstNonEmptyChild(div);
    if (!child) break;

    if (child.tagName === 'UL' || child.tagName === 'OL') {
      const firstItem = firstNonEmptyChild(child);
      if (firstItem?.tagName === 'LI') {
        div.innerHTML = firstItem.innerHTML;
        continue;
      }
      div.innerHTML = child.innerHTML;
      continue;
    }

    if (child.tagName === 'LI') {
      div.innerHTML = child.innerHTML;
      continue;
    }

    break; // <p>, <h1>, etc. are fine inside a <li>
  }

  // Remove any empty <li> nodes the AI may have appended.
  div.querySelectorAll('li').forEach((li) => {
    if (!li.textContent?.trim()) li.remove();
  });

  // If a top-level list still remains, return the first non-empty item's inner content.
  const firstTopLevelList = Array.from(div.children).find(
    (child) => child.tagName === 'UL' || child.tagName === 'OL',
  );
  if (firstTopLevelList) {
    const firstItem = firstNonEmptyChild(firstTopLevelList);
    if (firstItem?.tagName === 'LI') return firstItem.innerHTML.trim();
  }

  const li = div.querySelector('li');
  return li ? li.innerHTML.trim() : div.innerHTML.trim();
}

/**
 * The AI sometimes wraps multi-item results in <ul>/<ol>. Strip that outer
 * container — we insert directly into the existing list, so only the bare
 * <li> children are needed.
 */
function normalizeMultiItemHtml(html: string, ownerDoc: Document): string {
  const div = ownerDoc.createElement('div');
  div.innerHTML = html;
  // :scope > restricts to direct children so nested lists are not stripped.
  const list = div.querySelector(':scope > ul, :scope > ol');
  return list ? list.innerHTML.trim() : div.innerHTML.trim();
}

/**
 * Normalises the raw AI response before it is written back to the editor.
 *
 * ownerDoc comes from editor.view.dom.ownerDocument (not the global `document`)
 * so iframe-based editors work correctly.
 */
export function formatAiContent(content: string, mode: SelectionMode, ownerDoc: Document): string {
  const trimmed = content.trim();
  if (mode === 'singleListItem') return normalizeListHtml(trimmed, ownerDoc);
  if (mode === 'multiListItems') return normalizeMultiItemHtml(trimmed, ownerDoc);
  return trimmed; // 'document' and 'blockRange' are inserted verbatim
}

// ─── Range resolution ────────────────────────────────────────────────────────

/**
 * Clamps the selection to the content boundaries of a single <li> node.
 *
 * WHY CLAMP?
 * Tiptap adds a phantom boundary position after each Enter press in a list.
 * Without clamping the captured range can spill into the next item. The
 * `after(depth) - 1` removes that extra position.
 */
function trimSingleListItemSelection(
  doc: ProseMirrorNode,
  from: number,
  to: number,
  depth: number,
): Pick<SelectionContext, 'from' | 'to' | 'mode'> {
  const resolvedFrom = doc.resolve(from);
  const itemStart = resolvedFrom.before(depth) + 1; // first position inside <li>
  const maxTo = Math.max(itemStart, resolvedFrom.after(depth) - 1); // last position inside <li>

  return {
    from: Math.max(from, itemStart),
    to: Math.max(from, Math.min(to, maxTo)),
    mode: 'singleListItem',
  };
}

interface ListItemRange {
  from: number;
  to: number;
}

/** Returns the position range of every <li> in `listNode` that overlaps [from, to]. */
function collectListItemRanges(
  listNode: ProseMirrorNode,
  listContentStart: number,
  from: number,
  to: number,
): ListItemRange[] {
  const ranges: ListItemRange[] = [];
  let childPos = listContentStart;

  listNode.forEach((child) => {
    if (child.type.name !== 'listItem') {
      childPos += child.nodeSize;
      return;
    }
    const itemFrom = childPos;
    const itemTo = childPos + child.nodeSize;
    if (itemFrom < to && itemTo > from) ranges.push({ from: itemFrom, to: itemTo });
    childPos = itemTo;
  });

  return ranges;
}

/**
 * Resolves a selection that starts inside a list but ends outside it.
 *
 * NON-NESTED list (e.g. <ul><li>…</li></ul><p>after</p>):
 *   Returns blockRange with from = firstIntersectingItemFrom (inside <ul>).
 *   The apparent depth mismatch between `from` (inside the list) and `to` (outside)
 *   is resolved by DOMParser.parseSlice({ context: $from }), which produces the
 *   correct openStart/openEnd for replaceRange automatically.
 *
 * NESTED list (e.g. <ol><li><ul><li>…</li></ul></li></ol><p>after</p>):
 *   before(listDepth) lands inside an outer <li> (depth > 0), so there is no
 *   clean document-level boundary to expand to. Fall back to treating the
 *   outermost <li> as a singleListItem — safe, schema-valid, and avoids
 *   capturing unrelated content.
 */
function resolveCrossBoundaryRange(
  doc: ProseMirrorNode,
  resolvedFrom: ResolvedPos,
  from: number,
  to: number,
  listDepth: number,
  innermostListItemDepth: number,
  endBlockDepth: number | null,
  firstIntersectingItemFrom: number,
): Pick<SelectionContext, 'from' | 'to' | 'mode'> {
  const endTo = endBlockDepth === null ? to : doc.resolve(to).after(endBlockDepth);

  // For nested lists, before(listDepth) still lands inside an outer <li> (depth > 0).
  // Fall back to the outermost <li> as a single-item replacement.
  if (doc.resolve(resolvedFrom.before(listDepth)).depth > 0) {
    const outerDepth = outermostListItemDepth(resolvedFrom) ?? innermostListItemDepth;
    return trimSingleListItemSelection(doc, from, to, outerDepth);
  }

  // Non-nested: use the first intersecting <li>'s position so we only capture
  // what the user actually selected (not earlier items in the list).
  return { from: firstIntersectingItemFrom, to: endTo, mode: 'blockRange' };
}

/**
 * Main dispatcher — inspects where `from` and `to` sit in the document tree
 * and returns the correct mode + adjusted range.
 *
 * Decision tree:
 *  1. from NOT in any list  →  blockRange
 *  2. from and to in the same <li>  →  singleListItem
 *  3. from and to in different <li>s of the SAME list  →  multiListItems
 *  4. from in a list, to outside it  →  resolveCrossBoundaryRange
 *     (blockRange for flat lists; singleListItem fallback for nested lists)
 */
function resolveListSelection(
  doc: ProseMirrorNode,
  from: number,
  to: number,
): Pick<SelectionContext, 'from' | 'to' | 'mode'> {
  const resolvedFrom = doc.resolve(Math.max(1, from));
  const depth = listItemDepth(resolvedFrom); // innermost <li> depth, or null

  // Case 1: not in a list — positions used as-is.
  // DOMParser.parseSlice({ context: $from }) on the caller side handles any depth
  // mismatch automatically (inline within a block, full block, cross-boundary).
  if (depth === null) return { from, to, mode: 'blockRange' };

  const resolvedTo = doc.resolve(Math.max(1, Math.min(to, doc.content.size)));
  const toDepth = listItemDepth(resolvedTo);
  const sameItem = toDepth !== null && resolvedFrom.node(depth) === resolvedTo.node(toDepth);
  const listDepth = depth - 1; // depth of the <ul>/<ol> that contains this <li>

  // Case 2: both ends inside the same <li>.
  if (sameItem) return trimSingleListItemSelection(doc, from, to, depth);

  if (listDepth < 0) return { from, to, mode: 'blockRange' };

  const listNode = resolvedFrom.node(listDepth);
  const listContentStart = resolvedFrom.start(listDepth);
  const itemRanges = collectListItemRanges(listNode, listContentStart, from, to);

  if (itemRanges.length === 0) return { from, to, mode: 'blockRange' };

  const lastItemTo = itemRanges[itemRanges.length - 1].to;

  // Does the selection stay within the same list container?
  const endsAtListBoundary = to <= lastItemTo + 1;
  const endsInSameList =
    toDepth !== null &&
    toDepth - 1 === listDepth &&
    resolvedFrom.node(listDepth) === resolvedTo.node(toDepth - 1);

  if (itemRanges.length === 1) {
    if (!endsInSameList && !endsAtListBoundary) {
      // Case 4: started in a list, ended outside it.
      const endBlockDepth = nearestBlockDepth(resolvedTo);
      return resolveCrossBoundaryRange(
        doc,
        resolvedFrom,
        from,
        to,
        listDepth,
        depth,
        endBlockDepth,
        itemRanges[0].from,
      );
    }
    // Ends within the same list boundary — treat as single item.
    return trimSingleListItemSelection(doc, from, to, depth);
  }

  if (!endsInSameList && !endsAtListBoundary) {
    // Case 4 (multi-item): selection spans multiple items and escapes the list.
    const endBlockDepth = nearestBlockDepth(resolvedTo);
    return resolveCrossBoundaryRange(
      doc,
      resolvedFrom,
      from,
      to,
      listDepth,
      depth,
      endBlockDepth,
      itemRanges[0].from,
    );
  }

  // Case 3: multiple <li>s within the same list.
  return { from: itemRanges[0].from, to: lastItemTo, mode: 'multiListItems' };
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Returns the editor content from position 1 up to `to` as HTML.
 * Used by "Continue writing" to give the AI full preceding context in HTML
 * (as opposed to plain text, which loses formatting information).
 */
export function getHtmlBefore(editor: Editor, to: number): string {
  const doc = editor.state.doc;
  const pos = Math.max(1, Math.min(to, doc.content.size));
  const slice = doc.slice(1, pos);
  return stripAiHighlightMarks(getHTMLFromFragment(slice.content, editor.schema));
}

/**
 * Returns the full HTML content of the <li> that contains `from`.
 * We send the whole item (not just the visually selected text) so the AI has
 * enough context to produce a coherent rephrase.
 */
function getSingleListItemHtml(editor: Editor, from: number): string | null {
  const resolvedFrom = editor.state.doc.resolve(Math.max(1, from));
  const depth = listItemDepth(resolvedFrom);
  if (depth === null) return null;
  return stripAiHighlightMarks(
    getHTMLFromFragment(resolvedFrom.node(depth).content, editor.schema),
  );
}

/**
 * Entry point called when the AI menu opens with a non-empty selection.
 * Returns a SelectionContext describing what HTML to send to the AI and
 * where/how to write the result back once it arrives.
 */
export function captureSelectionContext(
  editor: Editor,
  from: number,
  to: number,
): SelectionContext {
  const doc = editor.state.doc;
  let selectionFrom = clampPos(doc, from);
  let selectionTo = clampPos(doc, to);
  if (selectionFrom > selectionTo) [selectionFrom, selectionTo] = [selectionTo, selectionFrom];

  // Ctrl+A gives from=0, to=doc.content.size — gap positions outside any content node.
  // mode='document' signals callers to use a whole-document replacement strategy.
  if (isDocumentSelection(doc, selectionFrom, selectionTo)) {
    return {
      html: stripAiHighlightMarks(editor.getHTML()),
      from: selectionFrom,
      to: selectionTo,
      mode: 'document',
    };
  }

  const range = resolveListSelection(doc, selectionFrom, selectionTo);

  // singleListItem sends the full <li> content for AI context;
  // all other modes slice the resolved range directly from the document.
  const html =
    range.mode === 'singleListItem'
      ? (getSingleListItemHtml(editor, range.from) ?? '')
      : stripAiHighlightMarks(
          getHTMLFromFragment(doc.slice(range.from, range.to).content, editor.schema),
        );

  return { html, ...range };
}
